// Video helpers for the admin builder. Lifted from
// src/web/src/routes/Builder.tsx — same pure-function shapes,
// no JSX, OK to duplicate rather than thread an admin/web
// shared package boundary.

/** Read a video file's duration in seconds by mounting a
 *  detached <video preload="metadata"> off an Object URL.
 *  Rejects if the browser can't parse the file. */
export function readVideoDurationSeconds(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement('video');
    el.preload = 'metadata';
    el.src = url;
    el.onloadedmetadata = () => {
      const dur = el.duration;
      URL.revokeObjectURL(url);
      if (!Number.isFinite(dur) || dur <= 0) {
        reject(new Error('Could not read duration'));
        return;
      }
      resolve(dur);
    };
    el.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Couldn't decode video"));
    };
  });
}

const POSTER_EXTRACT_TIMEOUT_MS = 8000;

/** Extract a single JPG frame ~100ms into the clip. */
export function extractPosterFrame(file: File): Promise<Blob> {
  return Promise.race([
    extractPosterFrameInner(file),
    new Promise<Blob>((_, reject) =>
      setTimeout(
        () => reject(new Error('poster extraction timed out')),
        POSTER_EXTRACT_TIMEOUT_MS,
      ),
    ),
  ]);
}

function extractPosterFrameInner(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.src = url;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    // Must attach to DOM (off-screen) so the browser actually
    // decodes — Safari skips work for detached elements.
    video.style.cssText =
      'position:fixed;left:-9999px;top:0;' +
      'width:1px;height:1px;opacity:0;pointer-events:none;';
    document.body.appendChild(video);

    const cleanup = () => {
      URL.revokeObjectURL(url);
      if (video.parentNode) video.parentNode.removeChild(video);
    };

    const draw = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 720;
        canvas.height = video.videoHeight || 1280;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          cleanup();
          reject(new Error('Canvas 2D unavailable'));
          return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            cleanup();
            if (blob) resolve(blob);
            else reject(new Error('toBlob returned null'));
          },
          'image/jpeg',
          0.82,
        );
      } catch (e) {
        cleanup();
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };

    video.onloadeddata = () => {
      video.currentTime = Math.min(0.1, (video.duration || 0.1) * 0.5);
    };
    video.onseeked = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => draw());
      });
    };
    video.onerror = () => {
      cleanup();
      reject(new Error("Couldn't decode video for poster"));
    };
  });
}

/** PUT a Blob to an R2 presigned URL with progress reporting. */
export function putToR2(
  url: string,
  body: Blob | File,
  contentType: string,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else
        reject(
          new Error(`PUT failed: ${xhr.status} ${xhr.statusText || ''}`),
        );
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(body);
  });
}

/** ffmpeg queue — single FFmpeg instance, serialize transcodes. */
let ffmpegChain: Promise<unknown> = Promise.resolve();
export function queueFfmpeg<T>(fn: () => Promise<T>): Promise<T> {
  const next = ffmpegChain.then(fn, fn);
  ffmpegChain = next.catch(() => {});
  return next;
}
